import { useMemo, useState } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PlotCard } from "../PlotCard";

interface Props {
  fromIso: string;
  toIso: string;
}

/**
 * Rolling-sum valve-open time + rolling-sum delivered volume binned by
 * chainage. Mirrors `Total_Delivery_at_Static_Chainage.png`. Bins use
 * the chainage of the box at firing time (so identical box visits at
 * different cutterhead chainages bin separately).
 */
export function StaticChainageChart({ fromIso, toIso }: Props) {
  const [binM, setBinM] = useState<number>(5);

  const series = useQuery({
    queryKey: ["lubrication", "series", fromIso, toIso],
    queryFn: () => api.lubrication.series({ from: fromIso, to: toIso }),
    refetchInterval: 60_000,
  });

  const data = useMemo(() => {
    const records = series.data?.records ?? [];
    const buckets = new Map<number, { time: number; vol: number }>();
    for (const r of records) {
      if (r.chainageM == null) continue;
      const bucket = Math.floor(r.chainageM / binM) * binM + binM / 2;
      let b = buckets.get(bucket);
      if (!b) {
        b = { time: 0, vol: 0 };
        buckets.set(bucket, b);
      }
      if (r.valveOpenS != null) b.time += r.valveOpenS;
      if (r.igvL != null) b.vol += r.igvL;
    }
    const xs: number[] = [];
    const ys1: number[] = [];
    const ys2: number[] = [];
    for (const [k, v] of [...buckets.entries()].sort((a, b) => a[0] - b[0])) {
      xs.push(k);
      ys1.push(v.time);
      ys2.push(v.vol);
    }
    return [
      {
        x: xs, y: ys1,
        type: "scatter", mode: "lines",
        name: "Sum valve-open (s)",
        line: { color: "#000", width: 1.2 },
        yaxis: "y",
      },
      {
        x: xs, y: ys2,
        type: "scatter", mode: "lines",
        name: "Sum volume (L)",
        line: { color: "#c8102e", width: 1.2 },
        yaxis: "y2",
      },
    ] as Record<string, unknown>[];
  }, [series.data, binM]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10.5px] tracking-wider uppercase text-dim">Bin</span>
        <Segmented
          value={String(binM) as `${number}`}
          onChange={(v) => setBinM(Number(v))}
          options={[
            { id: "1", label: "1 m" },
            { id: "3", label: "3 m" },
            { id: "5", label: "5 m" },
            { id: "10", label: "10 m" },
          ]}
        />
      </div>
      <PlotCard
        title="Total delivery at chainage"
        subtitle="Sum valve-open time + sum volume per bin"
        data={data as never}
        height={420}
        // fromIso encodes the active range; including it resets axes
        // on a 24h / 7d / Drive click.
        uirevision={`static-${binM}-${fromIso}`}
        layout={{
          margin: { l: 56, r: 64, t: 16, b: 36 },
          xaxis: { title: { text: "Chainage (m)" } },
          yaxis: { title: { text: "Time (s)" }, rangemode: "tozero" },
          yaxis2: {
            title: { text: "Volume (L)" },
            overlaying: "y", side: "right", showgrid: false,
            rangemode: "tozero",
          },
          legend: {
            x: 0.02, y: 0.98, xanchor: "left", yanchor: "top",
            bgcolor: "rgba(255,255,255,0.85)",
            bordercolor: "rgba(0,0,0,0.08)", borderwidth: 1,
            font: { size: 10, color: "#374151" },
          },
          showlegend: true,
        }}
      />
    </div>
  );
}

function Segmented<T extends string>({
  value, onChange, options,
}: { value: T; onChange: (v: T) => void; options: { id: T; label: string }[] }) {
  return (
    <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={clsx(
            "px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors",
            value === o.id ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
